import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { Hideable } from '@prisjakt/ui/hideable';

import type { AppState } from '../../../../reducers';

const BADGE_SIZE = 12;
const STORAGE_KEY = 'pj:changelogRead';

type Props = {
  noOutline?: boolean;
};

const Badge = ({ noOutline }: Props) => {
  const hasUnread = useCheckChangelogUnreadStatus();

  return <BadgeIndicator visible={hasUnread} $outline={!noOutline} />;
};

type IndicatorProps = {
  $outline: boolean;
};

export const BadgeIndicator = styled(Hideable)<IndicatorProps>`
  width: ${BADGE_SIZE}px;
  height: ${BADGE_SIZE}px;
  background: ${p => p.theme.colors.yellove500};
  border-radius: 99px;
  position: absolute;
  top: -${BADGE_SIZE / 10}px;
  right: -${BADGE_SIZE / 10}px;
  box-shadow: ${p => (p.$outline ? `0 0 0 2px ${p.theme.header.bg}` : 'none')};
  transition: transform 150ms ease;
  z-index: 1;
`;

const selectChangelogArticles = (state: AppState) =>
  state.areas.changelog.articles.map(a => a.id);

const selectChangelogOpen = (state: AppState) => state.areas.changelog.isOpen;

export function useCheckChangelogUnreadStatus() {
  const ids = useSelector(selectChangelogArticles);
  const isOpen = useSelector(selectChangelogOpen);
  const [unread, setUnread] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setReadUpdates();
    }

    if (!isOpen) {
      setUnreadStatus();
    }
  }, [isOpen]);

  const setUnreadStatus = useCallback(() => {
    try {
      const read = localStorage.getItem(STORAGE_KEY)?.split(';') || [];

      setUnread(ids.filter(id => !read.includes(id)));
    } catch {} // eslint-disable-line no-empty
  }, [ids]);

  const setReadUpdates = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, ids.join(';'));
      setUnread([]);
    } catch {} // eslint-disable-line no-empty
  }, [ids]);

  return Boolean(unread.length);
}

export default Badge;
